import { useEffect, useRef, useState } from "react";
import type { ProductData } from "@/mocks/products";

interface Props {
  product: ProductData;
}

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function ProductDetails({ product }: Props) {
  const { ref, inView } = useInView(0.1);

  const rows = [
    { label: "Material", value: product.details.material },
    { label: "Stone", value: product.details.stone },
    { label: "Type", value: product.details.type },
    { label: "Collection", value: product.details.collection },
    { label: "Price", value: product.details.price },
  ];

  return (
    <section className="relative bg-[#060508] py-40 px-6 overflow-hidden">
      {/* Top line */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-20"
        style={{ background: `linear-gradient(to bottom, transparent, ${product.accentColor}15)` }}
      />

      <div ref={ref} className="max-w-3xl mx-auto">
        <div
          style={{
            opacity: inView ? 1 : 0,
            transition: "opacity 1.5s ease",
          }}
        >
          <div
            className="text-xs tracking-[0.5em] uppercase mb-8 text-center"
            style={{ color: `${product.accentColor}50` }}
          >
            Details
          </div>
          <h2
            className="text-3xl md:text-4xl font-light text-white/70 mb-16 text-center leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.04em" }}
          >
            素材と記録
          </h2>
        </div>

        {/* Details table */}
        <div
          className="relative"
          style={{
            border: `1px solid ${product.accentColor}12`,
            boxShadow: `0 0 60px ${product.glowColor}`,
          }}
        >
          {rows.map((row, i) => (
            <div
              key={i}
              className="flex items-center justify-between px-10 py-6"
              style={{
                borderBottom: i < rows.length - 1 ? `1px solid ${product.accentColor}08` : "none",
                opacity: inView ? 1 : 0,
                transition: `opacity 1.8s ease ${0.2 + i * 0.15}s`,
              }}
            >
              <span
                className="text-xs tracking-[0.3em] uppercase"
                style={{ color: `${product.accentColor}40` }}
              >
                {row.label}
              </span>
              <span
                className="text-base font-light text-white/50"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  letterSpacing: "0.05em",
                  color: row.label === "Price" ? product.accentColor : undefined,
                  fontSize: row.label === "Price" ? "1.25rem" : undefined,
                }}
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
